import * as React from 'react';
import { Theme, Typography } from '@mui/material';
import { WithStyles } from '@mui/styles';
import createStyles from '@mui/styles/createStyles';
import withStyles from '@mui/styles/withStyles';
import { useTranslation } from 'services/localization/useTranslation';
import { AvailabilityStatusBubble } from 'components/AvailabilityStatusBubble';
import { AvailabilityStatusType } from 'services/types';

interface OwnProps {
	availability: number | null | undefined;
	quantity: number | null | undefined;
	isLoadingAvailability: Boolean;
	availabilityNotLoadedForLongDuration: Boolean;
}

type Props = OwnProps & WithStyles<typeof styles>;

const AvailabilityStatus = (props: Props) => {
	const {
		classes,
		availability,
		quantity,
		isLoadingAvailability,
		availabilityNotLoadedForLongDuration,
	} = props;

	const { t } = useTranslation();

	const getAvailabilityStatus = (): AvailabilityStatusType => {
		if (isLoadingAvailability || availabilityNotLoadedForLongDuration) {
			return 'loading';
		}
		if (quantity === undefined || availability === undefined) {
			return 'unavailable';
		}
		if (quantity === null || availability === null) {
			return 'available';
		}
		if (availability <= 0 || quantity <= 0) {
			return 'unavailable';
		}
		const availabilityToQuantityRatio = availability / quantity;
		if (availabilityToQuantityRatio >= 0.5) {
			return 'available';
		}
		if (availabilityToQuantityRatio > 0) {
			return 'low';
		}
		return 'unavailable';
	};

	const getAvailabilityStatusString = () => {
		if (availabilityNotLoadedForLongDuration) {
			return t('newRental.notCalculated', 'Not calculated');
		}
		if (isLoadingAvailability) {
			return t('common:states.loading') + '...';
		}
		if (quantity === undefined || availability === undefined) {
			return t('common:states.notAvailable', 'Not available');
		} else if (availability != null && quantity != null) {
			return availability + ' / ' + quantity;
		} else {
			return '∞';
		}
	};

	return (
		<React.Fragment>
			<div className={classes.container}>
				<AvailabilityStatusBubble status={getAvailabilityStatus()} marginSmall={true} />
				<div>
					<Typography variant="body2">{getAvailabilityStatusString()}</Typography>
				</div>
			</div>
		</React.Fragment>
	);
};

const styles = (theme: Theme) =>
	createStyles({
		container: {
			display: 'flex',
			justifyContent: 'center',
			padding: theme.spacing(1.5),
		},
		centered: {
			display: 'flex',
			flexDirection: 'row',
			justifyContent: 'flex-start',
			alignItems: 'center',
		},
		warning: {
			color: theme.palette.colors.availabilityNone.main,
		},
	});

export default withStyles(styles)(AvailabilityStatus);
